import { useEffect, useId, useRef, useState } from "react";
import { Icone } from "./Icone.js";
import { IconeAbrirMenu } from "./icones.js";

export type OpcaoMultipla = {
  valor: string;
  rotulo: string;
  /** Quantos itens a opção alcança. Vai em mono, à direita. */
  contagem: number;
};

/**
 * Um campo de seleção **múltipla**, com caixa por opção e contagem.
 *
 * Ele existe porque a tela de montar sessão tinha listas de caixas soltas, uma
 * seção por eixo: matéria, assunto, tipo e dificuldade, cada uma num cartão
 * próprio. Em três matérias isso já era uma tela inteira de rolagem antes do
 * botão de começar, e o que a pessoa faz ali é uma escolha rápida — não a
 * leitura de um catálogo.
 *
 * **O gatilho diz o estado, não o rótulo.** "Todas", o nome da única escolhida,
 * ou "3 selecionadas". Quem olha de relance precisa saber se o filtro está
 * ligado sem abrir o menu.
 *
 * **A contagem fica em mono, à direita de cada opção.** Um filtro que promete
 * "Difícil" e devolve zero itens parece defeito; a contagem é o que impede a
 * pessoa de montar uma sessão vazia para descobrir.
 *
 * `popover` nativo pela mesma razão dos menus do editor: ele pinta na *top
 * layer*, fora de qualquer `overflow` que recorte, e traz de graça fechar ao
 * clicar fora, fechar no Esc e um só aberto por vez.
 */
export function SeletorMultiplo({
  rotulo,
  opcoes,
  escolhidos,
  aoMudar,
  vazio = "Todas",
  desabilitado,
  idDoRotulo,
}: {
  rotulo: string;
  opcoes: readonly OpcaoMultipla[];
  escolhidos: readonly string[];
  aoMudar: (novos: string[]) => void;
  /** O que o gatilho mostra com nada escolhido. */
  vazio?: string;
  desabilitado?: boolean;
  /** Quem desenha o rótulo por fora (a montagem do Caderno) passa o id dele. */
  idDoRotulo?: string;
}) {
  const id = useId().replace(/:/g, "");
  const painel = useRef<HTMLDivElement>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const caixa = painel.current;
    const alvo = gatilho.current;
    if (!caixa || !alvo) return;
    const aoAlternar = (e: Event) => {
      const evento = e as ToggleEvent;
      const abrindo = evento.newState === "open";
      if (abrindo) {
        // A posição vem de medida: na top layer não há pai a que se ancorar.
        // `setProperty` é CSSOM, e CSSOM não é o que a CSP barra.
        const r = alvo.getBoundingClientRect();
        caixa.style.setProperty("--menu-topo", `${Math.round(r.bottom)}px`);
        caixa.style.setProperty("--menu-esquerda", `${Math.round(r.left)}px`);
        caixa.style.setProperty("--menu-largura", `${Math.round(r.width)}px`);
      }
      setAberto(abrindo);
    };
    caixa.addEventListener("beforetoggle", aoAlternar);
    return () => caixa.removeEventListener("beforetoggle", aoAlternar);
  }, []);

  const escolhidasVisiveis = opcoes.filter((o) => escolhidos.includes(o.valor));
  const resumo =
    escolhidasVisiveis.length === 0
      ? vazio
      : escolhidasVisiveis.length === 1
        ? escolhidasVisiveis[0]!.rotulo
        : `${escolhidasVisiveis.length} selecionadas`;

  function alternar(valor: string) {
    aoMudar(escolhidos.includes(valor) ? escolhidos.filter((v) => v !== valor) : [...escolhidos, valor]);
  }

  return (
    <div className={idDoRotulo ? "seletor-multiplo" : "campo seletor-multiplo"}>
      {!idDoRotulo && (
        <span className="campo__rotulo" id={`rot-${id}`}>
          {rotulo}
        </span>
      )}
      <button
        type="button"
        ref={gatilho}
        className="seletor-multiplo__gatilho"
        aria-labelledby={idDoRotulo ?? `rot-${id}`}
        aria-haspopup="true"
        aria-expanded={aberto}
        disabled={desabilitado || opcoes.length === 0}
        popoverTarget={`menu-${id}`}
      >
        <span className="seletor-multiplo__resumo">{opcoes.length === 0 ? "—" : resumo}</span>
        <Icone glifo={IconeAbrirMenu} papel="acao" tamanho="p" />
      </button>

      <div id={`menu-${id}`} popover="auto" ref={painel} className="seletor-multiplo__menu" aria-label={rotulo}>
        {escolhidos.length > 0 && (
          <button type="button" className="botao botao--texto seletor-multiplo__limpar" onClick={() => aoMudar([])}>
            Limpar
          </button>
        )}
        <ul className="seletor-multiplo__lista">
          {opcoes.map((o) => (
            <li key={o.valor}>
              <label className="seletor-multiplo__opcao">
                <input
                  type="checkbox"
                  className="filtro__caixa"
                  checked={escolhidos.includes(o.valor)}
                  onChange={() => alternar(o.valor)}
                />
                <span className="seletor-multiplo__nome">{o.rotulo}</span>
                <span className="dado seletor-multiplo__contagem">{o.contagem}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
